import { createRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "@/hooks/useSafeTranslation";
import { BarChart2, Zap, Link2, AlertTriangle, Sparkles } from "lucide-react";
import { authenticatedRoute } from "../_authenticated";
import { StatCard, SkeletonCard } from "@/components/ui";
import { useUsage } from "@/hooks/useUsage";
import { useCrmStatus } from "@/hooks/useCrmStatus";
import { useBillingStatus } from "@/hooks/useBillingStatus";
import { useAuth } from "@/contexts/useAuth";
import { usePaywallShown } from "@/hooks/usePaywallShown";
import { cn, toErrorDisplayMessage, toDisplayString, toSafeReactNode } from "@/lib/utils";
import { resolveViewState } from "@/lib/query-state";

const PROVIDER_IDS = ["hubspot", "pipedrive", "salesforce", "weclapp"] as const;

function OverviewPage() {
  const { t } = useTranslation("common");
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const { data: usage, isLoading: usageLoading, error: usageError } = useUsage();
  const { data: connections, isLoading: crmLoading, error: crmError } = useCrmStatus();
  const { data: billing } = useBillingStatus();

  /**
   * Both queries that feed a NUMBER on this page decide the page state together. Reading
   * only `data` made a failed request indistinguishable from an empty account: with every
   * call failing the overview told a paying customer with a connected HubSpot and 400
   * captures "Kein CRM verbunden · Erfassungen heute 0". A `503 missing_config` on a fresh
   * deploy renders exactly that.
   */
  const viewState = resolveViewState([
    { isLoading: usageLoading, error: usageError },
    { isLoading: crmLoading, error: crmError },
  ]);

  const connectedSet = new Set((connections ?? []).map((c) => c.provider));
  const isPastDue = billing?.status === "past_due";
  const noCrmConnected = (connections ?? []).length === 0;
  const isFirstCapture =
    usage != null && usage.capturesToday + usage.capturesThisMonth === 0;

  // The same expression the offer below is rendered from, so the event cannot describe a
  // paywall the user was not shown. See hooks/usePaywallShown.ts.
  usePaywallShown(usage != null && !usage.canCapture, token, "usage_limit");

  if (viewState.kind === "loading") {
    return (
      <div className="space-y-6">
        <h1 className="text-xl font-semibold tracking-tight text-text">{toSafeReactNode(t("overview.title"))}</h1>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
        <div className="rounded-lg border border-border bg-surface p-4">
          <SkeletonCard />
        </div>
      </div>
    );
  }

  // Same treatment the usage page already uses — an unreachable API says so instead of
  // rendering an empty account the customer does not have.
  if (viewState.kind === "error") {
    return (
      <div className="space-y-6">
        <h1 className="text-xl font-semibold tracking-tight text-text">{toSafeReactNode(t("overview.title"))}</h1>
        <div className="flex items-center gap-3 rounded-lg border border-danger/30 bg-danger/10 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-danger" aria-hidden />
          <p className="text-sm text-danger">
            {toSafeReactNode(
              toErrorDisplayMessage(viewState.error) || toDisplayString(t("errors.generic"))
            )}
          </p>
        </div>
      </div>
    );
  }

  // Past this point both queries succeeded, so a missing number is genuinely a missing
  // number and no longer a swallowed request failure.
  const capturesTodayLabel =
    usage?.capturesPerDayLimit != null
      ? `${toDisplayString(usage.capturesToday)} / ${toDisplayString(usage.capturesPerDayLimit)}`
      : toDisplayString(usage?.capturesToday);
  const capturesMonthLabel =
    usage?.capturesPerMonthLimit != null
      ? `${toDisplayString(usage.capturesThisMonth)} / ${toDisplayString(usage.capturesPerMonthLimit)}`
      : toDisplayString(usage?.capturesThisMonth);

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold tracking-tight text-text">{toSafeReactNode(t("overview.title"))}</h1>

      {isPastDue && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-400">
          <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden />
          <div>
            <p className="font-medium">{toSafeReactNode(t("overview.pastDueTitle"))}</p>
            <p className="text-amber-400/80">
              {toSafeReactNode(t("overview.pastDueDescription"))}{" "}
              <Link to="/billing" className="underline">
                {toSafeReactNode(t("overview.billingLink"))}
              </Link>
              .
            </p>
          </div>
        </div>
      )}

      {noCrmConnected && (
        <div className="flex items-center gap-3 rounded-lg border border-accent/30 bg-accent/10 p-4 text-sm text-accent">
          <Link2 className="h-5 w-5 shrink-0" aria-hidden />
          <div>
            <p className="font-medium">{toSafeReactNode(t("overview.noCrmTitle"))}</p>
            <p className="text-accent/80">
              <Link to="/crm" className="underline">
                {toSafeReactNode(t("overview.connectCrm"))}
              </Link>
              {" "}{toSafeReactNode(t("overview.noCrmDescription"))}
            </p>
          </div>
        </div>
      )}

      {isFirstCapture && !noCrmConnected && (
        <div className="flex items-center gap-3 rounded-lg border border-accent/30 bg-accent/10 p-4 text-sm text-accent">
          <Sparkles className="h-5 w-5 shrink-0" aria-hidden />
          <div>
            <p className="font-medium">{toSafeReactNode(t("overview.firstCaptureTitle"))}</p>
            <p className="text-accent/80">{toSafeReactNode(t("overview.firstCaptureDescription"))}</p>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          label={toDisplayString(t("overview.capturesToday"))}
          value={capturesTodayLabel}
          icon={<Zap className="h-4 w-4" />}
        />
        <StatCard
          label={toDisplayString(t("overview.capturesMonth"))}
          value={capturesMonthLabel}
          icon={<BarChart2 className="h-4 w-4" />}
        />
        <StatCard
          label={toDisplayString(t("overview.connectedCrms"))}
          value={connectedSet.size}
          trend={toDisplayString(t("overview.ofCount", { count: PROVIDER_IDS.length }))}
          icon={<Link2 className="h-4 w-4" />}
        />
      </div>

      {usage && !usage.canCapture && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-400">
          <p className="font-medium">{toSafeReactNode(t("overview.limitReachedTitle"))}</p>
          <p className="mt-1 text-amber-400/80">
            {toSafeReactNode(toErrorDisplayMessage(usage.upgradeCopy) || toDisplayString(t("overview.limitReachedDescription")))}{" "}
            <Link to="/billing" className="underline">
              {toSafeReactNode(t("overview.upgradeNow"))}
            </Link>
          </p>
        </div>
      )}

      <section className="rounded-lg border border-border bg-surface p-4">
        <h2 className="mb-3 text-sm font-medium text-text-muted">
          {toSafeReactNode(t("overview.crmStatus"))}
        </h2>
        <ul className="space-y-2">
          {PROVIDER_IDS.map((id) => {
            const connected = connectedSet.has(id);
            return (
              <li
                key={toDisplayString(id)}
                className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface-2 px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "h-2 w-2 shrink-0 rounded-full",
                      connected ? "bg-success" : "bg-text-subtle"
                    )}
                    aria-hidden
                  />
                  <span className="text-sm font-medium text-text">{toSafeReactNode(t(`overview.provider_${id}` as "overview.provider_hubspot"))}</span>
                </div>
                {connected ? (
                  <span className="text-xs text-text-muted">{toSafeReactNode(t("overview.connected"))}</span>
                ) : (
                  <Link
                    to="/crm"
                    className="text-xs text-accent hover:underline"
                  >
                    {toSafeReactNode(t("overview.connect"))}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}

export const indexRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/",
  component: OverviewPage,
});
